import moment from 'moment'
import React, { useEffect, useState, ReactNode, useContext } from 'react'
import _, { cloneDeep, result, set } from 'lodash'
import DateService from 'live/services/date'
import Authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import {
    getDataset,
    uploadDataset,
    getWellSyncStatus,
    triggerOnDemnadSync,
    getExtensionConfigs,
    clearDataset,
    getInterventionInfo,
    getDatasetStats
} from '../api'
import globalConfirmation from 'live/services/global-confirmation'
import { PopUpContent } from '../styles/styles'
import i18n from 'live/services/i18n'

export interface DvDRow {
    datetime: string
    days_cum: number
    timestamp: number
    bit_depth_ft: number
    hole_depth_ft: number
    rigstate: string
}

export const emptyDvDDataset = Array.apply(null, Array(1)).map(() => ({
    datetime: null,
    days_cum: null,
    timestamp: null,
    bit_depth_ft: null,
    hole_depth_ft: null,
    rigstate: null
}))

export function PopUpMessage(message, isDanger: boolean) {
    return (
        <PopUpContent className={isDanger ? 'modal-confirmation-alert' : ''}>
            <strong className="mb-15">{message}</strong>
            {isDanger && <strong className="msg-error">This action cannot be undone.</strong>}
        </PopUpContent>
    )
}

export const showDeletePopup = (
    wellId,
    datasetType,
    setData,
    setLoadingDataset,
    setUnSavedData,
    setlastDataEditTime,
    unSavedData
) => {
    globalConfirmation.confirm(
        unSavedData
            ? PopUpMessage(
                  i18n(
                      'Are you sure you want to delete this Days vs Depth data? Doing so, you will lose your changes.'
                  ),
                  true
              )
            : PopUpMessage(i18n('Are you sure you want to delete this Days vs Depth data?'), true),
        () => {
            deleteData(wellId, datasetType, setData, setLoadingDataset, setUnSavedData, setlastDataEditTime)

            return true
        },
        {
            btnText: i18n('Yes, clear'),
            cancelText: i18n('cancel'),
            btnTextApplying: i18n('Clearing')
        },
        () => {
            return true
        }
    )
}

export const showUpdatePopup = (
    canNotify,
    from,
    wellId,
    datasetType,
    setData,
    setUnSavedData,
    setLastDataEditTime,
    setLoadingDataset
) => {
    globalConfirmation.warn(
        PopUpMessage(
            i18n(
                'While you were editing, new data points are updated through data sync/manual update. Do you want to refresh the data? Doing so, you will lose your changes.'
            ),
            false
        ),
        () => {
            getDataAndUpdateUI(
                canNotify,
                from,
                wellId,
                datasetType,
                setData,
                setUnSavedData,
                setLastDataEditTime,
                setLoadingDataset
            )
            return true
        },
        {
            btnText: i18n('Yes, refresh'),
            cancelText: i18n('continue editing'),
            btnTextApplying: i18n('Refreshing data')
        },
        () => {
            return true
        }
    )
}

export const getElementForCell = _.memoize(
    function (row, col) {
        const div = document.createElement('div')
        div.style.display = 'flex'
        div.style.width = '100%'
        div.style.height = '100%'
        div.style.justifyContent = 'center'
        div.style.alignItems = 'center'

        return div
    },
    function (row, col) {
        return ''.concat(row).concat(col)
    }
)

// export const addCalculatedDvDColumn =

export const addCalculatedDvDColumns = (sourceData): DvDRow[] => {
    const convertedData = _.cloneDeep(sourceData)
    const startTimestamp = parseInt(convertedData[0]?.timestamp)

    if (!startTimestamp || !_.isNumber(startTimestamp)) return sourceData

    let prevTime = -999
    let cumDays = 0
    let prevCumDays = -999

    for (let i = 0; i < _.size(sourceData); i++) {
        const entry = convertedData[i]
        const timestamp = parseInt(entry?.timestamp)
        prevTime = prevTime === -999 ? timestamp : prevTime
        try {
            if (!!timestamp && _.isFinite(timestamp)) {
                _.set(entry, 'datetime', DateService.getMoment(timestamp).format('YYYY-MM-DD HH:mm:ss Z'))
                if (!entry?.rigstate.toString().toLowerCase().includes('well break')) {
                    cumDays = cumDays + _.round((timestamp - prevTime) / 1000 / 60 / 60 / 24, 2)
                    _.set(entry, 'days_cum', Math.round(cumDays * 100) / 100)
                } else {
                    _.set(entry, 'days_cum', Math.round(prevCumDays * 100) / 100)
                }
            } else {
                _.set(entry, 'datetime', null)
                _.set(entry, 'days_cum', null)
            }
        } catch (error) {
            console.error('Error while performing calculation : ' + error)
        }
        prevTime = timestamp
        prevCumDays = cumDays
    }
    return convertedData
}

export const getDataFromHotTable = refTableInstance => {
    const d = refTableInstance.current.hotInstance.getSourceData()

    return d
}

export const addDvDChangeLogs = (data, rowIndex) => {
    try {
        data[rowIndex].isEdited = true
        data[rowIndex].changeLog = { changeBy: Authorization.user.displayName, changeAt: Date.now() }
    } catch (error) {
        console.error(error)
    }
    return data
}

export const getRowCountFromHotTable = refTableInstance => {
    const nonEmptyRowCount = refTableInstance.current?.hotInstance.countRows()
        ? refTableInstance.current?.hotInstance.countRows()
        : 0
    const emptyRowCount = refTableInstance.current?.hotInstance.countEmptyRows()
        ? refTableInstance.current?.hotInstance.countEmptyRows()
        : 0
    return nonEmptyRowCount - emptyRowCount
}

export const roundTime = date => {
    date.setHours(date.getHours() + Math.round(date.getMinutes() / 60))
    date.setMinutes(0, 0, 0)

    return date.getTime()
}

export const saveDataset = (
    containsUnSavedData,
    refTable,
    wellId,
    datasetType,
    setLoadingDataset,
    setUnSavedData,
    setlastDataEditTime
) => {
    if (containsUnSavedData) {
        setLoadingDataset(true)
        const d = getDataFromHotTable(refTable).map(a => {
            return { ...a }
        })
        d.forEach(function (v) {
            delete v.datetime
            delete v.days_cum
        })
        uploadDataset(wellId, datasetType, d)
            .then(result => result.json())
            .then(response => {
                if (response.responseCode === 'OK') {
                    globalNotification.success('Dataset saved successfully')
                    nullifyNewDataPoints(setUnSavedData, setlastDataEditTime)
                } else {
                    globalNotification.error(response.message ?? 'An error prevented the data from being saved')
                }
                setLoadingDataset(false)
            })
            .catch(e => globalNotification.error(e.message ?? 'An error prevented the data from being saved'))
            .finally(() => setLoadingDataset(false))
    } else {
        globalNotification.error("Everything is up to date.There's nothing new to save ")
    }
}

export const deleteData = (wellId, datasetType, setData, setLoadingDataset, setUnSavedData, setlastDataEditTime) => {
    clearDataset(wellId, datasetType)
        .then(result => result.json())
        .then(response => {
            if (response.responseCode === 'OK') {
                if (!response) return

                getDataAndUpdateUI(
                    true,
                    'Clear Dataset',
                    wellId,
                    datasetType,
                    setData,
                    setUnSavedData,
                    setlastDataEditTime,
                    setLoadingDataset
                )
                globalNotification.success('Dataset cleared successfully')
            } else {
                globalNotification.error(response.message ?? 'An error prevented the data from being saved')
            }
            setLoadingDataset(false)
        })
        .catch(e => {
            globalNotification.error(e.message ?? "We're having trouble clearing the dataset")
        })
        .finally(() => true)
}

export const nullifyNewDataPoints = (setUnSavedData, setlastDataEditTime) => {
    setUnSavedData(false)
    setlastDataEditTime(Date.now())
}

export const getDataAndUpdateUI = (
    canNotify,
    from,
    wellId,
    datasetType,
    setData,
    setUnSavedData,
    setlastDataEditTime,
    setLoadingDataset
) => {
    console.log('Get Data triggered from ' + from)
    const controller = new AbortController()
    const signal = controller.signal
    setLoadingDataset(true)
    getDataset(signal, wellId, datasetType)
        .then(result => result.json())
        .then(response => {
            if (response.responseCode === 'OK') {
                if (!response) return

                if (response.result && response.result.data && response.result.data.length) {
                    setData(addCalculatedDvDColumns(response.result.data))
                } else {
                    setData(addCalculatedDvDColumns(emptyDvDDataset))
                }
                nullifyNewDataPoints(setUnSavedData, setlastDataEditTime)
            } else {
                if (canNotify) {
                    globalNotification.error(response.message ?? "We're having trouble retrieving the dataset")
                }
            }
            setLoadingDataset(false)
        })
        .catch(e => {
            if (canNotify) {
                globalNotification.error(e.message ?? "We're having trouble retrieving the dataset")
            }
            setLoadingDataset(false)
        })
        .finally(() => setLoadingDataset(false))
}

export const getStats = (wellId, datasetType, startIndex, endIndex, setLoadingDataset, setDatasetStats) => {
    const controller = new AbortController()
    const signal = controller.signal
    setLoadingDataset(true)
    getDatasetStats(signal, wellId, datasetType, startIndex, endIndex)
        .then(result => result.json())
        .then(response => {
            if (response.responseCode === 'OK') {
                if (!response) return

                if (response.result) {
                    setDatasetStats(response.result)
                } else {
                    return
                }
            } else {
                globalNotification.error(response.message ?? "We're having trouble retrieving the dataset statistics")
                return
            }
        })
        .catch(e => {
            globalNotification.error(e.message ?? "We're having trouble retrieving the dataset statistics")
            // setLoadingDataset(false)
            return
        })
        .finally(() => setLoadingDataset(false))
}

export const timeAgoInTwoLevels = (timeInMillis: number) => {
    try {
        const now = Date.now()
        const diffInMinutes = (now - timeInMillis) / 1000 / 60
        const getValueFromDiffTime = (
            value: number,
            divisor: number,
            secondOrderMultiplier: number,
            firstUnit: string,
            secondUnit: string
        ) => {
            const q = parseInt((value / divisor).toString())
            const r = Math.round((value % divisor) / secondOrderMultiplier)
            const firstResult = `${q} ${firstUnit}${q > 1 ? 's' : ''}`
            let secondResult = ``
            if (r > 1) {
                secondResult = ` and ${r} ${secondUnit}${r > 1 ? 's' : ''}`
            }
            return `${firstResult}${secondResult} ago`
        }

        if (diffInMinutes / 525600 >= 1) {
            //Years and Months
            return getValueFromDiffTime(diffInMinutes, 525600, 43800, 'year', 'month')
        } else if (diffInMinutes / 43800 >= 1) {
            //Months and Days
            return getValueFromDiffTime(diffInMinutes, 43800, 1440, 'month', 'day')
        } else if (diffInMinutes / 1440 >= 1) {
            //Days and Hours
            return getValueFromDiffTime(diffInMinutes, 1440, 60, 'day', 'hour')
        } else if (diffInMinutes / 60 >= 1) {
            //Hours and Minutes
            return getValueFromDiffTime(diffInMinutes, 60, 1, 'hour', 'minute')
        } else if (diffInMinutes < 60 && diffInMinutes >= 1) {
            //Minutes and Seconds
            return getValueFromDiffTime(diffInMinutes * 60, 60, 1, 'minute', 'second')
        } else {
            return 'a few seconds ago'
        }
    } catch {
        return 'an unknown time ago'
    }
}

export const constructWellSyncStatus = d => {
    const syncSource = d.result.whoTriggeredMe
    if (d.result.currentStatus === 'Completed' || (d.result.currentStatus === 'Idle' && d.result.lastRunTime > 0)) {
        return 'Sync successfully completed ' + timeAgoInTwoLevels(d.result.lastRunTime) + ' [' + syncSource + ']'
    } else if (d.result.currentStatus === 'Skipped') {
        return d.result.whyDidISkipLast + ' [' + syncSource + ']'
    } else if (d.result.currentStatus === 'Idle') {
        return 'Sync is in queue' + ' [' + syncSource + ']'
    } else if (d.result.currentStatus.includes('Error')) {
        return 'Error while running sync' + ' [' + syncSource + ']. Please check'
    } else if (d.result.currentStatus === 'Inactive Well') {
        return (
            'Well is not active and Auto Sync is disabled. Last Sync completed ' +
            timeAgoInTwoLevels(d.result.lastRunTime) +
            ' [' +
            syncSource +
            ']'
        )
    }
}

export const runDvDStatusScheduler = (
    wellId,
    setTicking,
    lastNewDataSyncTime,
    setlastNewDataSyncTime,
    unSavedData,
    setTableData,
    lastDataEditTime,
    setlastDataEditTime,
    setWellSyncStatus,
    setLoadingDataset,
    datasetType,
    showErrorsForStatus,
    setShowErrorsForStatus,
    setUnSavedData
) => {
    const controller = new AbortController()
    const signal = controller.signal
    setTicking(false)
    getWellSyncStatus(signal, wellId)
        .then(result => result.json())
        .then(response => {
            if (response.responseCode === 'OK') {
                let enteredZero = false
                if (lastNewDataSyncTime === 0) {
                    enteredZero = true
                    setlastNewDataSyncTime(response.result.lastRunTime)
                }
                if (
                    response.result.capturedDataPoints > 0 &&
                    response.result.lastRunTime > lastNewDataSyncTime &&
                    !enteredZero
                ) {
                    if (!unSavedData) {
                        getDataAndUpdateUI(
                            showErrorsForStatus,
                            'Scheduler - New Appends WP',
                            wellId,
                            datasetType,
                            setTableData,
                            setUnSavedData,
                            setlastDataEditTime,
                            setLoadingDataset
                        )
                    } else {
                        showUpdatePopup(
                            showErrorsForStatus,
                            'Scheduler - New Appends',
                            wellId,
                            datasetType,
                            setTableData,
                            setUnSavedData,
                            setlastDataEditTime,
                            setLoadingDataset
                        )
                    }
                }

                if (response.result.lastManualSaveTime > lastDataEditTime) {
                    setlastDataEditTime(response.result.lastManualSaveTime)
                    if (!unSavedData) {
                        getDataAndUpdateUI(
                            showErrorsForStatus,
                            'Scheduler - Data Edit WP',
                            wellId,
                            datasetType,
                            setTableData,
                            setUnSavedData,
                            setlastDataEditTime,
                            setLoadingDataset
                        )
                    } else {
                        showUpdatePopup(
                            showErrorsForStatus,
                            'Scheduler - Data Edits',
                            wellId,
                            datasetType,
                            setTableData,
                            setUnSavedData,
                            setlastDataEditTime,
                            setLoadingDataset
                        )
                    }
                }
                setlastNewDataSyncTime(response.result.lastRunTime)
                setWellSyncStatus(() => {
                    return constructWellSyncStatus(response)
                })
                setShowErrorsForStatus(true)
            } else {
                if (showErrorsForStatus) {
                    globalNotification.error(response.message ?? "We're having trouble retrieving the dataset")
                }
                setShowErrorsForStatus(false)
            }
        })
        .catch(e => {
            setWellSyncStatus(() => {
                return constructWellSyncErrorStatus(e, showErrorsForStatus)
            })
            setShowErrorsForStatus(false)
        })
        .finally(() => {
            setTicking(true)
        })
    setTicking(true)
}

export const constructWellSyncErrorStatus = (e, canNotify = true) => {
    if (e.responseCode === 'NOT_FOUND') {
        return 'The automatic sync isn\'t working right now. Wait for it to catch up or start the sync manually'
    } else {
        if (canNotify) {
            globalNotification.error(
                e.message ?? 'We encountered an issue during the last data sync. Please review for details'
            )
        }
        return 'We encountered an issue during the last data sync. Please review for details'
    }
}

export const triggerSync = async (
    wellId,
    start,
    end,
    clearData,
    datasetType,
    setLoadingDataset,
    setForcingSync,
    setData,
    setWellSyncStatus,
    setUnSavedData,
    setlastDataEditTime,
    setlastNewDataSyncTime,
    forceRefresh,
    setShowReprocessModal
) => {
    const controller = new AbortController()
    const signal = controller.signal
    setForcingSync(true)
    getExtensionConfigs(signal)
        .then(result => result.json())
        .then(obj => {
            if (!obj || !obj.extensions || !obj.extensions.length) {
                globalNotification.error(
                    'There are no settings for syncing your data. Review the options for Days vs Depth Sync Integration'
                )
                setForcingSync(false)
                return
            }
            const dvdConfigurations = obj.extensions.filter(x => x.type === 'dvd-config' && x.active)
            if (!dvdConfigurations || !dvdConfigurations.length || dvdConfigurations.length === 0) {
                globalNotification.error(
                    'There are no settings for syncing your data. Review the options for Days vs Depth Sync Integration'
                )
                setForcingSync(false)
                return
            }
            const requestBody = dvdConfigurations.map(x => x.config)
            const qualifierList = dvdConfigurations.map(x => x.qualifier)
            for (let i = 0, l = requestBody.length; i < l; i++) {
                const obj = requestBody[i]
                obj.qualifier = qualifierList[i]
            }
            triggerOnDemnadSync(wellId, start, end, clearData, requestBody)
                .then(result => result.json())
                .then(response => {
                    if (response.responseCode === 'OK') {
                        if (response.result.capturedDataPoints > 0 || forceRefresh) {
                            getDataAndUpdateUI(
                                true,
                                'Force Sync',
                                wellId,
                                datasetType,
                                setData,
                                setUnSavedData,
                                setlastDataEditTime,
                                setLoadingDataset
                            )
                        }
                        setWellSyncStatus(() => {
                            return constructWellSyncStatus(response)
                        })
                        setlastNewDataSyncTime(response.result.lastRunTime)
                        setForcingSync(false)
                        setShowReprocessModal(false)
                        globalNotification.success('On-demand data synchronization is completed successfully')
                    } else {
                        globalNotification.error(response.message ?? "We're having trouble retrieving the dataset")
                        setForcingSync(false)
                        //setShowReprocessModal(false)
                    }
                })
                .catch(e => {
                    globalNotification.error(
                        e.message ?? 'The dataset refresh failed.Try refreshing the data again in a few minutes '
                    )
                    setWellSyncStatus(() => {
                        return constructWellSyncErrorStatus(e)
                    })
                    setForcingSync(false)
                })
        })
        .catch(e => {
            globalNotification.error(
                e.message ?? 'The dataset refresh failed.Try refreshing the data again in a few minutes'
            )
            setForcingSync(false)
        })
        .finally(() => true)
}

export const classNames = classes => {
    return Object.entries(classes)
        .filter(([key, value]) => value)
        .map(([key, value]) => key)
        .join(' ')
}

export const getVPHeight = () => {
    try {
        const e = document.querySelector('section[class*=AssetProfile__AssetProfileScrollContentSection]')
        if (e && e.offsetHeight && e.offsetHeight > 0) {
            return e.offsetHeight
        }
    } catch (e) {
        return document.documentElement.clientHeight
    }
    return document.documentElement.clientHeight
}

export const getAndFilterIntervention = (wellId: string) => {
    return new Promise(resolve => {
        const controller = new AbortController()
        const signal = controller.signal
        getInterventionInfo(signal, wellId)
            .then(result => {
                if (result.ok) {
                    return result.json()
                } else {
                    globalNotification.error(
                        "We're having trouble getting intervention information for the well right now. Please try again later"
                    )
                    resolve('')
                }
            })
            .then(response => {
                if (!response || !response.interventions) {
                    return false
                }
                resolve(Object.values(response.interventions).map(x => x.intervention))
            })
            .catch(e => {
                globalNotification.error(
                    e.message ??
                        "We're having trouble getting intervention information for the well right now. Please try again later"
                )
                resolve('')
            })
    })
}

export const showReprocessMenu = async (
    interventions,
    setInterventions,
    tableData,
    wellId,
    setShowReprocessModal,
    reprocessParameters,
    setReprocessParameters
) => {
    await getAndFilterIntervention(wellId)
        .then(interventionCollection => {
            // if (!interventionCollection || interventionCollection.length === 0) {
            //     globalNotification.error('The well do not have any interventions available. Please add interventions')
            //     return
            // }

            const filteredInterventions = interventionCollection.filter((element, index, array) => {
                return element.status === 'active' || element.status === 'finished'
            })
            setInterventions(interventionCollection)
            const v = {
                lastDataPointIndex: 0,
                intervention: filteredInterventions,
                begin: 0,
                end: 0
            }

            // Getting data from table
            if (tableData.length > 0) {
                v.lastDataPointIndex = tableData.map(x => x.timestamp).reverse()[0]
                v.begin = v.lastDataPointIndex
            }
            setReprocessParameters(v)
            setShowReprocessModal(true)
        })
        .finally(() => {
            return true
        })
}

export const getInterventionTitle = (intervention: Record<string, unknown>) => {
    const n = intervention?.name || 'Unknown Intervention'
    const b = new Date(intervention?.begin || 0).toLocaleString()
    const e = new Date(intervention?.end || 0).toLocaleString()
    const t = intervention?.type || 'Unknown Type'
    const s = intervention?.status || 'Unknown Status'
    const isGoodIntervention = intervention?.begin > 0 && intervention?.begin < intervention?.end
    return (
        <div>
            <span style={{ color: 'black', fontWeight: 'bold' }}>{n}&nbsp;-&nbsp;</span>
            <span style={{ color: 'black' }}>{`[${t}/${s}]`}&nbsp;&nbsp;&nbsp;</span>
            {isGoodIntervention && (
                <span style={{ color: 'gray' }}>
                    {b} - {e}
                </span>
            )}
            {!isGoodIntervention && (
                <span style={{ color: 'red', fontWeight: 'bold' }}>Invalid intervention begin/end dates</span>
            )}
        </div>
    )
}

const constructDatasetStatsMessage = (data: Record<string, unknown>) => {
    if (data === null || data.numberOfRows === 0) return ''

    if (data.numberOfModifiedRows !== 0) {
        return `The selected range contains ${data.numberOfRows} ${
            data.numberOfRows === 1 ? `row` : `rows`
        } out of which ${data.numberOfModifiedRows} ${
            data.numberOfModifiedRows === 1 ? `row is` : `rows are`
        } modified manually`
    } else {
        return `The selected range contains ${data.numberOfRows} ${data.numberOfRows === 1 ? `row` : `rows`}`
    }
}

export const getReprocessTimespan = (start, end, render, type, data) => {
    if (render) {
        const isGoodIntervention = start > 0 && start < end
        let messageType = ''
        if (type == 'Active Intervention') {
            if (start > 0 && start == end) {
                messageType = 'Data is calculated until intervention end date already'
            } else {
                messageType = 'Invalid intervention begin/end dates'
            }
        } else if (type == 'All Interventions') {
            messageType = 'Invalid intervention begin/end dates'
        } else if (type == 'Custom Time Range') {
            if (start == 0 || end == 0) {
                messageType = 'Start date and End date must be filled and it should be valid'
            } else if (start >= end) {
                messageType = 'End date should be greater than start date'
            }
        }

        let dataStats = {
            numberOfRows: 0,
            numberOfModifiedRows: 0
        }
        if (type == 'Custom Time Range' && isGoodIntervention) {
            if (data && data.length > 0) {
                try {
                    let clonedData = _.clone(data) as Array<Record<string, unknown>>
                    clonedData = clonedData.filter(x => x['timestamp'] >= start && x['timestamp'] <= end)
                    dataStats['numberOfRows'] = clonedData.length

                    clonedData = clonedData.filter(x => x && x['changeLog'] && x['changeLog']['changeBy'] !== 'System')
                    dataStats['numberOfModifiedRows'] = clonedData.length
                } catch {
                    dataStats = {
                        numberOfRows: 0,
                        numberOfModifiedRows: 0
                    }
                }
            }
        }

        const b = new Date(start || 0).toLocaleString()
        const e = new Date(end || 0).toLocaleString()
        return (
            <div>
                {isGoodIntervention && (
                    <div>
                        <span style={{ color: 'black', fontWeight: 'normal' }}>Reprocessing from&nbsp;</span>
                        <span style={{ color: 'black', fontWeight: 'bold' }}>{b}&nbsp;</span>
                        <span style={{ color: 'black', fontWeight: 'normal' }}>to&nbsp;</span>
                        <span style={{ color: 'black', fontWeight: 'bold' }}>{e}</span>
                        <br></br>
                        {type == 'Custom Time Range' && dataStats !== null && (
                            <span style={{ color: 'orange', fontWeight: 'bold' }}>
                                {constructDatasetStatsMessage(dataStats)}
                            </span>
                        )}
                    </div>
                )}
                {!isGoodIntervention && (
                    <div>
                        <span style={{ color: 'red', fontWeight: 'bold' }}>{messageType}</span>
                    </div>
                )}
            </div>
        )
    } else {
        return <div></div>
    }
}

export const isInterventionValid = intervention => {
    try {
        if (intervention.status === 'scenario' || intervention.begin === 0 || intervention.end === 0) {
            return false
        }

        return true
    } catch (e) {
        return false
    }
}

export const getCauseForInvalidIntervention = intervention => {
    const result = Array<string>()
    try {
        if (intervention.status === 'scenario') {
            result.push('Planned Intervention')
        }
        if (intervention.begin === 0 || intervention.end === 0) {
            result.push('Invalid Intervention Span')
        }
    } catch (e) {
        result.push('Unknown Cause')
    }

    if (result.length > 0) {
        return (
            'Disabled due to ' +
            result.reduce((text, value, i, array) => text + (i < array.length - 1 ? ', ' : ' and ') + value)
        )
    } else {
        return ''
    }
}
